import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import {
  fetchLocationsForDropdown,
  fetchLocationProductionRoomsForDropdown,
} from "../../shared/locationDropdowns";
import {
  buildStartManufacturingPayload,
  buildUpdateManufacturedProductPayload,
  getManufacturedProductApiErrorMessage,
} from "../../shared/manufacturedProductPayload";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { getInProcessMonitoringStateFromApiResponse } from "../../shared/utility";

export function ManufactureNewProductViewModel(logout, addSnackbar) {
  const [certifiedByOptions, setCertifiedByOptions] = useState([]);
  const [claimsOptions, setClaimsOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [productionRoomOptions, setProductionRoomOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [printLabelDetails, setPrintLabelDetails] = useState({
    production_date: null,
    product_plu: null,
    product_name: null,
    product_description: null,
    product_weight: null,
    lot_number: null,
    supervisor: null,
    product_allergens: null,
    barcodeHtml: null,
    barcode: null,
  });
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState({});
  const [printType, setPrintType] = useState(0);
  const [manufacturedProduct, setManufacturedProduct] = useState(null);
  const [inProcessMonitoringHistory, setInProcessMonitoringHistory] =
    useState(null);

  const getProductsList = async function (search) {
    const payload = {
      page: 1,
      search: search,
    };
    let callResult = await makePostApiCall(`products`, payload);

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getAssemblyItemReceivingByBarcode = async function (search, page) {
    const payload = {
      manufacture: true,
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`receivingInvoiceIndex`, payload);

    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCertifiedByOptionsForDropdown = async function () {
    let callResult = await makePostApiCall(`certifiedByOptionsForDropdown`);

    if (callResult.success) {
      setCertifiedByOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const makeApiCallToStartManufacturing = async function (productValues) {
    let callResult = await makePostApiCall(
      "startManufacturingProduct",
      buildStartManufacturingPayload(productValues),
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(getManufacturedProductApiErrorMessage(callResult));
    }
  };

  const getClaimsForDropdown = async function () {
    let callResult = await makePostApiCall(`claimsForDropdown`);

    if (callResult.success) {
      setClaimsOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getLocationsForDropdown = async function () {
    const callResult = await fetchLocationsForDropdown();

    if (callResult.success) {
      setLocationOptions(callResult.data || []);
      return callResult.data || [];
    }

    if (callResult.errorStatus === 401) {
      logout();
    }

    return [];
  };

  const loadProductionRoomsForLocation = async function (location) {
    if (!location?.value) {
      setProductionRoomOptions([]);
      return [];
    }

    const callResult = await fetchLocationProductionRoomsForDropdown(location);

    if (callResult.success) {
      const options = callResult.data || [];
      setProductionRoomOptions(options);
      return options;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }

    setProductionRoomOptions([]);
    return [];
  };

  const updateManufacturedProduct = async function (productValues) {
    const payload = buildUpdateManufacturedProductPayload(productValues);
    let updateProductResult = await makePostApiCall(
      "updateManufacturedProduct",
      payload,
    );
    if (updateProductResult.success) {
      if (selectedRecipe.recipe) {
        const recipeIngredientsResult = await makePostApiCall(
          "saveOrUpdateManufactureIngredients",
          {
            ...selectedRecipe,
            manufactured_product_id: payload.manufactured_product_id,
          },
        );
        if (recipeIngredientsResult.success) {
          return updateProductResult;
        } else {
          if (recipeIngredientsResult.errorStatus === 401) {
            logout();
          }
          showErrorModal(
            getManufacturedProductApiErrorMessage(recipeIngredientsResult),
          );
          return recipeIngredientsResult;
        }
      }
      return updateProductResult;
    } else {
      if (updateProductResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(getManufacturedProductApiErrorMessage(updateProductResult));
    }
    return updateProductResult;
  };

  const addIngredientToManufacturedProduct = async function (
    ingredientValues,
    isEdit,
  ) {
    let callResult = await makePostApiCall(
      isEdit
        ? "updateManufacturedProductIngredientById/" + ingredientValues.id
        : "addIngredientToManufacturedProduct",
      ingredientValues,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManufacturedProductIngredients = async function (
    manufactedProductId,
  ) {
    let callResult = await makePostApiCall(
      `getManufacturedProductIngredients/${manufactedProductId}`,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getReceivingAssemblyItemInStockByName = async function (search) {
    let callResult = await makePostApiCall(
      `getReceivingAssemblyItemInStockByName`,
      {
        search: search,
      },
    );

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const deleteIngredientFromManufacturedProduct = async function (
    ingredientId,
  ) {
    let callResult = await makePostApiCall(
      `deleteIngredientFromManufacturedProduct/${ingredientId}`,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  const addManualEntryForManufacturingProduct = async function (productValues) {
    let callResult = await makePostApiCall(
      `addManualEntryToManufacturedProduct`,
      productValues,
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManufacturedProductLabel = async function (manufacturedProductId) {
    let callResult = await makePostApiCall(
      `getManufacturedProductLabel/${manufacturedProductId}`,
    );
    if (callResult.success) {
      setPrintType(1);
      setPrintLabelDetails(callResult.data);

      setTimeout(() => {
        window.print();
      }, 1500);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      if (type === 1) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRecipesForDropdown = async function () {
    let callResult = await makePostApiCall(`recipesForDropdown`);

    if (callResult.success) {
      setRecipes(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRecipeItems = async function (id, batchAmount) {
    let callResult = await makePostApiCall(`fetchRecipeForUse/${id}`);

    if (callResult.success) {
      const newRecipe = {
        recipe: callResult.data.recipe,
        recipeItems: [
          ...callResult.data.recipeItems,
          ...callResult.data.packagingItems,
        ],
      };

      calculateAmountRequiredForRecipeItems(batchAmount, newRecipe);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  function calculateAmountRequiredForRecipeItems(
    batchAmount,
    recipe = selectedRecipe,
  ) {
    if (!recipe || !recipe.recipeItems) return;
    const updatedRecipeItems = recipe.recipeItems.map((item) => ({
      ...item,
      amount_required: (
        parseFloat(batchAmount) * parseFloat(item.recipe_per_kg)
      ).toFixed(2),
    }));
    setSelectedRecipe({ ...recipe, recipeItems: updatedRecipeItems });
  }

  const updateInProcessMonitoringHistory = async function (payload) {
    let callResult = await makePostApiCall(
      "inProcessHistoryOnNextStatus",
      payload,
    );
    if (callResult.success) {
      const statusRecord = getInProcessMonitoringStateFromApiResponse(
        callResult.data,
      );
      if (statusRecord) {
        setInProcessMonitoringHistory(statusRecord);
      }
      addSnackbar(
        new SnackbarData(
          Date.now().toString(),
          callResult.data?.message ||
            (statusRecord?.in_process_status_name
              ? `Product status moved to ${statusRecord.in_process_status_name}`
              : "Status updated"),
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            Date.now().toString(),
            "Failed to update status",
            SnackbarType.Error,
          ),
        );
      }
    }
  };

  const previousInProcessMonitoringHistory = async function (payload) {
    let callResult = await makePostApiCall(
      "inProcessHistoryOnPreviousStatus",
      payload,
    );
    if (callResult.success) {
      const statusRecord = getInProcessMonitoringStateFromApiResponse(
        callResult.data,
      );
      if (statusRecord) {
        setInProcessMonitoringHistory(statusRecord);
      }
      addSnackbar(
        new SnackbarData(
          Date.now().toString(),
          callResult.data?.message ||
            (statusRecord?.in_process_status_name
              ? `Product status moved to ${statusRecord.in_process_status_name}`
              : "Status updated"),
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            Date.now().toString(),
            "Failed to update status",
            SnackbarType.Error,
          ),
        );
      }
    }
  };

  return {
    addIngredientToManufacturedProduct,
    certifiedByOptions,
    deleteIngredientFromManufacturedProduct,
    emptyModal,
    getAssemblyItemReceivingByBarcode,
    getCertifiedByOptionsForDropdown,
    getManufacturedProductIngredients,
    getProductsList,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    updateManufacturedProduct,
    getReceivingAssemblyItemInStockByName,
    makeApiCallToStartManufacturing,
    addManualEntryForManufacturingProduct,
    getManufacturedProductLabel,
    printLabelDetails,
    employeeOptions,
    getUsersForDropdown,
    fetchRecipesForDropdown,
    recipes,
    fetchRecipeItems,
    selectedRecipe,
    setSelectedRecipe,
    calculateAmountRequiredForRecipeItems,
    showErrorModal,
    manufacturedProduct,
    claimsOptions,
    getClaimsForDropdown,
    supervisorOptions,
    locationOptions,
    productionRoomOptions,
    getLocationsForDropdown,
    loadProductionRoomsForLocation,
    inProcessMonitoringHistory,
    updateInProcessMonitoringHistory,
    previousInProcessMonitoringHistory,
    setInProcessMonitoringHistory,
  };
}
